<template>
  <div class="sticky-image-text">
    <div class="sticky-image-text__col" ref="sticky" :style="style">
      
      <div ref="expandable" class="sticky-image-text__image-box">
        <div
          class="sticky-image-text__item__image"
          v-for="(item, index) in items"
          ref="image"
          :key="`sit-${index}-image`"
          :style="{ 'background-image': `url(${item.image})` }"
        >
        </div>
      </div>
    </div>

    <div class="sticky-image-text__col" :style="{ marginBottom: bottomClearenceString }">
      <scroll-speed :speed="1.2">
        <img slot-scope="{}" class="sticky-image-text__hero" :src="heroImage" />
      </scroll-speed>
      
      <div
        class="sticky-image-text__item__text"
        v-for="(item, index) in items"
        ref="item"
        :key="`sit-${index}-text`"
      >
        <scroll-speed :speed="0.75">
          <div slot-scope="{}">
            <p class="sticky-image-text__item__index">0{{ index + 1 }}</p>
            <h3 class="sticky-image-text__item__title">{{ item.text.title }}</h3>
            <p class="sticky-image-text__item__paragraph">{{ item.text.paragraph }}</p>
          </div>
        </scroll-speed>
      </div>

    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

// mixins
import Sticky from "./../mixins/sticky";
// components
import ScrollSpeed from "@/components/scroll-speed";

export default {
  name: "StickyImageText",
  mixins: [Sticky],
  components: {ScrollSpeed},
  props: {
    content: Object
  },
  data() {
    return {
      // DOM
      heroImage: this.content.heroImage,
      items: this.content.items,

      // settings
      activeAreaSize: 33, // percent from middle to be fading the text
      expandImageDuration: 800
    };
  },
  computed: {
    ...mapState({
      currentScroll: state => state.globals.currentScroll,
      screenHeight: state => state.globals.screenHeight
    }),
    // 'state'
    bottomClearenceString() {
      return `${this.expandImageDuration * 1.5}px`;
    }
  },
  watch: {
    currentScroll: {
      handler(scroll) {
        this.fadeTheElements();
        this.expandLastImage(scroll);
      }
    }
  },
  methods: {
    isNotMobile() {
      return window.innerWidth > 440;
    },
    fadeTheElements() {
      this.$refs.item.forEach((item, i) => {
        const getPxFromViewportCenter =
          item.getBoundingClientRect().top -
          this.halfScreen() +
          item.getBoundingClientRect().height / 2;
        const inPercents = this.pxToPercent(
          getPxFromViewportCenter,
          this.halfScreen()
        );
        const ofActiveArea = this.mapActiveArea(inPercents);
        const singleDigit = this.mapToSingleDigit(ofActiveArea);
        const absoluteValue = this.makeAbsolute(singleDigit);
        const absoluteValueInverted = this.invert(absoluteValue);
        
        const singleDigitInverted = this.invert(singleDigit);
        const imageOpacity = singleDigitInverted + 1;

        const textOpacity =
          absoluteValueInverted > -1 ? absoluteValueInverted + 1 : 0;
        const imageOpacityCapped =
          imageOpacity > 0 && imageOpacity < 1 // if inside active area
            ? imageOpacity // be calculated value
            : imageOpacity > 1 // we scrolled past active area
            ? 1 // be 1
            : 0; // else be 0, = we havn't scrolled long enough

        // fade the images
        item.style.opacity = textOpacity;
        if (i != 0) this.$refs.image[i].style.opacity = imageOpacityCapped;
      });
    },

    expandLastImage() {
      // the image is 100% of its container,
      // the container is 50% of vieport,
      // so when the image is 200% wide, it's fullscreen/done

      // last text element
      const lastItem = this.$refs.item[this.$refs.item.length - 1];
      const expandableBox = this.$refs.expandable;
      const isScrolledPast =
        lastItem.getBoundingClientRect().bottom < this.halfScreen();

      // default width
      if (!isScrolledPast) {
        if (this.isNotMobile()) {
          expandableBox.style.width = "100%";
        } else {
          expandableBox.style.height = "50%";
        }
        return;
      }

      // px value how far last text is scrolled past
      const amountScrolledPassed =
        (lastItem.getBoundingClientRect().bottom - this.halfScreen()) * -1;
      const lastImage = this.$refs.image[this.$refs.image.length - 1]; // scale this one

      const mappedAmound = this.pxToPercent(
        amountScrolledPassed,
        this.expandImageDuration
      );

      const zeroToOne = this.mapToSingleDigit(mappedAmound);

      // max cap
      if (amountScrolledPassed > this.expandImageDuration) {
        if (this.isNotMobile()) {
          expandableBox.style.width = "200%";
        } else {
          expandableBox.style.height = "100%";
        }
        return;
      }

      //
      if (this.isNotMobile()) {
        expandableBox.style.width =
          this.easeInOutQuad(zeroToOne) * 100 + 100 + "%";
      } else {
        expandableBox.style.height =
          this.easeInOutQuad(zeroToOne) * 50 + 50 + "%";
      }
      lastImage.style.transform = `scale(${1.2 -
        this.easeInOutQuad(zeroToOne) * 0.2})`;
    },

    // Math helpers:

    halfScreen() {
      return this.screenHeight / 2;
    },

    // converts px value -> % value
    pxToPercent(px, percOf) {
      return px / (percOf / 100);
    },

    // makes activeAreaSize the new 100% a.k.a. max/min
    // i.e. the active area becomes more narrow
    mapActiveArea(percent) {
      return percent / (this.activeAreaSize / 100);
    },

    // converts value -100-0-100 -> -1-0-1
    mapToSingleDigit(percent) {
      return percent / 100;
    },

    // returns int as positive
    makeAbsolute(int) {
      return Math.abs(int);
    },

    // negative -> positive and viceverca
    invert(int) {
      return int * -1;
    },

    // acceleration until halfway, then deceleration
    // use with an input between 0-1
    easeInOutQuad(t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },

    // acceleration until halfway, then deceleration
    easeInOutQuint(t) {
      return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.fadeTheElements(this.currentScroll);
      this.expandLastImage(this.currentScroll);
    });
  }
};
</script>

<style lang="scss">
@import "../scss/globals.scss";

.sticky-image-text {
  @extend %page-margin-1;
  @extend %page-margin-1-v;

  padding-top: 0;

  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  @media screen and (min-width: 440px) {
    flex-wrap: nowrap;
  }

  &__col {
    @extend %page-margin-1-v;
    min-height: 100%;

    position: relative;

    // fading images container
    &:first-of-type {
      //  position: sticky;
      align-self: flex-start;
      top: 0;

      z-index: 1;

      width: 100%;
      height: 100vh;
      box-sizing: border-box;

      margin-right: 0;

      @media screen and (min-width: 440px) {
        margin-right: 1.25rem;
        width: 50%;
      }
      @media screen and (min-width: 769px) {
        margin-right: 2.5rem;
      }
      @media screen and (min-width: 1440px) {
        margin-right: 5rem;
      }
      @media screen and (min-width: 2480px) {
        margin-right: 10rem;
      }
    }

    // fading text container
    &:last-of-type {
      width: 100%;
      padding-bottom: 50vh;
      @media screen and (min-width: 440px) {
        width: 45%;
      }
    }
  }

  &__hero {
    max-width: 100%;
    height: auto;

    position: relative;
    z-index: 1;
  }

  &__image-box {
    position: relative;
    height: 50%;
    overflow: hidden;

    @media screen and (min-width: 440px) {
      height: 100%;
    }
  }

  &__item {
    &__image {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;

      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;

      opacity: 0;
      &:first-of-type {
        opacity: 1;
      }

      &:last-of-type {
        height: 100vh;
        background-position: top;
        transform: scale(1.2);

        @media screen and (min-width: 440px) {
          height: none;
          width: 100vw;
          background-position: left;
        }
      }
    }

    &__text {
      @extend %page-margin-2-v;

      opacity: 0;
    }

    &__index {
      @include typeset(trumpet);
      color: lighten(black, 40%);
    }

    &__title {
      @include typeset(display-3);
      @extend %title-margin-1;
    }

    &__paragraph {
      @include typeset(body-large);
      max-width: 28em;
      color: lighten(black, 40%);
    }
  }
}
</style>
